import { useEffect, useRef } from "react";

export function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { el?.classList.add("cs-in"); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add("cs-in"); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`cs-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>{children}</Tag>;
}

export function SectionHead({ eyebrow, title, sub, id }) {
  return (
    <Reveal className="max-w-2xl">
      {eyebrow && <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-rose-500">{eyebrow}</p>}
      <h2 id={id} className="font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900 mt-1.5 leading-tight">{title}</h2>
      {sub && <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

const CHIP = { now: ["Verfügbar", "bg-emerald-50 text-emerald-700 ring-emerald-200"], soon: ["Bald", "bg-amber-50 text-amber-700 ring-amber-200"], always: ["Immer", "bg-rose-50 text-rose-700 ring-rose-200"] };

export function InfoCard({ icon: Icon, title, text, status, i = 0, testId }) {
  const chip = status && CHIP[status];
  return (
    <Reveal delay={i * 70} data-testid={testId}
      className="group relative h-full rounded-3xl bg-white/85 backdrop-blur-xl border border-pink-100 p-5 sm:p-6 shadow-[0_10px_30px_-18px_rgba(225,29,99,.35)] hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(225,29,99,.5)] hover:border-pink-200 transition-[transform,box-shadow,border-color] duration-300">
      <div className="flex items-start justify-between gap-3">
        <span className="w-11 h-11 rounded-2xl pink-gradient text-white flex items-center justify-center shadow-md shadow-rose-300/50 group-hover:scale-105 transition-transform"><Icon className="w-5 h-5" /></span>
        {chip && <span className={`shrink-0 h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-extrabold ring-1 ${chip[1]}`} data-testid={`${testId}-status`}>{chip[0]}</span>}
      </div>
      <h3 className="mt-4 font-display text-base sm:text-lg font-extrabold text-slate-900 leading-snug">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{text}</p>
    </Reveal>
  );
}

export function CardGrid({ items, cols = "sm:grid-cols-2 lg:grid-cols-3", prefix }) {
  return (
    <div className={`mt-6 grid grid-cols-1 ${cols} gap-4 sm:gap-5`}>
      {items.map(([icon, title, text, status], i) => <InfoCard key={title} icon={icon} title={title} text={text} status={status} i={i} testId={`${prefix}-card-${i}`} />)}
    </div>
  );
}

export function BigHeart({ size = "w-36 h-36 sm:w-44 sm:h-44", testId }) {
  return (
    <div className={`relative ${size} mx-auto`} data-testid={testId} aria-hidden>
      <span className="absolute inset-0 rounded-full cs-heart-halo" />
      <svg viewBox="0 0 24 24" className="relative w-full h-full cs-heart-beat drop-shadow-[0_18px_30px_rgba(157,20,70,.45)]">
        <defs>
          <linearGradient id="cs-heart" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" /><stop offset=".45" stopColor="#ffd1e1" /><stop offset="1" stopColor="#fb7aa8" /></linearGradient>
        </defs>
        <path fill="url(#cs-heart)" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        <path fill="#fff" opacity=".55" d="M7.2 5.4c-1.5.2-2.7 1.5-2.8 3 0 .3.4.4.5.1.4-1.1 1.3-1.9 2.4-2.2.4-.1.3-.9-.1-.9z" />
      </svg>
    </div>
  );
}
